import React, { useMemo } from 'react';

export type WbsStatus5 = 'NOT_STARTED' | 'ON_TRACK' | 'AT_RISK' | 'DELAYED' | 'COMPLETED';
export type StatusDist5 = Record<WbsStatus5, number>;

const LABEL: Record<WbsStatus5, string> = {
    NOT_STARTED: 'Not started',
    ON_TRACK: 'On track',
    AT_RISK: 'At risk',
    DELAYED: 'Delayed',
    COMPLETED: 'Completed',
};

const HINT: Record<WbsStatus5, string> = {
    NOT_STARTED: 'EV = 0',
    ON_TRACK: 'SPI ≥ 1.00',
    AT_RISK: '0.90 ~ 1.00',
    DELAYED: 'SPI < 0.90',
    COMPLETED: 'EV = 100',
};

const COLOR: Record<WbsStatus5, { stroke: string; dot: string; row: string }> = {
    COMPLETED: { stroke: '#16a34a', dot: 'bg-green-600', row: 'hover:bg-green-50' },
    ON_TRACK: { stroke: '#2563eb', dot: 'bg-blue-600', row: 'hover:bg-blue-50' },
    AT_RISK: { stroke: '#f59e0b', dot: 'bg-amber-500', row: 'hover:bg-amber-50' },
    DELAYED: { stroke: '#ef4444', dot: 'bg-red-500', row: 'hover:bg-red-50' },
    NOT_STARTED: { stroke: '#6b7280', dot: 'bg-gray-500', row: 'hover:bg-gray-50' },
};

function clamp0(x: number) {
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, x);
}

function round1(x: number) {
    return Math.round(x * 10) / 10;
}

function buildSegments(dist: StatusDist5) {
    const order: WbsStatus5[] = ['COMPLETED', 'ON_TRACK', 'AT_RISK', 'DELAYED', 'NOT_STARTED'];
    const raw = order.map((k) => ({ k, v: clamp0(dist[k] ?? 0) }));
    const sum = raw.reduce((a, b) => a + b.v, 0);
    if (sum <= 0) return raw.map((x) => ({ ...x, pct: 0 }));
    return raw.map((x) => ({ ...x, pct: (x.v / sum) * 100 }));
}

export default function StatusDistributionDonutCardV2({
    dist,
    onClickStatus,
}: {
    dist: StatusDist5;
    onClickStatus?: (s: WbsStatus5) => void;
}) {
    const normalized = useMemo(() => buildSegments(dist), [dist]);

    const size = 236;
    const strokeW = 28;
    const r = (size - strokeW) / 2;
    const c = 2 * Math.PI * r;

    let acc = 0;

    const attention = round1((dist.DELAYED ?? 0) + (dist.AT_RISK ?? 0));
    const completed = round1(dist.COMPLETED ?? 0);

    return (
        <div className="h-full w-full min-w-0 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm">
            <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">Status Distribution</div>
                    <div className="mt-1 text-[11px] text-gray-500">가중치(weight) 기준 상태 분포</div>
                </div>

                <div className="shrink-0 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                    Completed {completed}%
                </div>
            </div>

            {/*
              기본: 세로 배치
              충분히 넓을 때만 2열
              오른쪽 legend는 고정폭이 아니라 max-width만 사용
            */}
            <div className="grid min-w-0 grid-cols-1 gap-4 min-[1350px]:grid-cols-[minmax(0,1fr)_minmax(0,260px)]">
                <div className="min-w-0">
                    <div className="flex w-full items-center justify-center">
                        <div className="relative shrink-0">
                            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                                <circle
                                    cx={size / 2}
                                    cy={size / 2}
                                    r={r}
                                    fill="none"
                                    stroke="#e5e7eb"
                                    strokeWidth={strokeW}
                                />

                                {normalized.map(({ k, pct }) => {
                                    const len = (pct / 100) * c;
                                    const dasharray = `${len} ${c - len}`;
                                    const dashoffset = -((acc / 100) * c);
                                    acc += pct;

                                    if (pct <= 0) return null;

                                    return (
                                        <circle
                                            key={k}
                                            cx={size / 2}
                                            cy={size / 2}
                                            r={r}
                                            fill="none"
                                            stroke={COLOR[k].stroke}
                                            strokeWidth={strokeW}
                                            strokeDasharray={dasharray}
                                            strokeDashoffset={dashoffset}
                                            transform={`rotate(-90 ${size / 2} ${size / 2})`}
                                            className={onClickStatus ? 'cursor-pointer' : undefined}
                                            onClick={onClickStatus ? () => onClickStatus(k) : undefined}
                                        />
                                    );
                                })}
                            </svg>

                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="text-[11px] text-gray-500">Attention</div>
                                <div className="text-2xl font-semibold text-gray-900">{attention}%</div>
                                <div className="text-[10px] text-gray-400">At risk + Delayed</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="min-w-0 w-full min-[1350px]:max-w-[260px] min-[1350px]:justify-self-end">
                    <div className="grid min-w-0 grid-cols-1 gap-2">
                        {(['COMPLETED', 'ON_TRACK', 'AT_RISK', 'DELAYED', 'NOT_STARTED'] as WbsStatus5[]).map((k) => {
                            const pct = round1(dist[k] ?? 0);

                            return (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={onClickStatus ? () => onClickStatus(k) : undefined}
                                    className={`w-full min-w-0 rounded-xl border px-3 py-2 text-left transition ${COLOR[k].row}`}
                                >
                                    <div className="flex min-w-0 items-start gap-3">
                                        <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${COLOR[k].dot}`} />

                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 items-center justify-between gap-3">
                                                <span className="min-w-0 text-sm font-medium text-gray-800">
                                                    {LABEL[k]}
                                                </span>
                                                <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                                                    {pct}%
                                                </span>
                                            </div>

                                            <div className="mt-0.5 break-words text-[11px] leading-4 text-gray-500">
                                                {HINT[k]}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}